import React, { useEffect, useState, useRef } from 'react'
import { Card, Table, Tag, Tooltip, Button, Col, Modal, message, Input, Row } from 'antd';
import { EyeOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import apiClient from 'services/apiClient';
import { PlusOutlined, CloudDownloadOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useMyContext } from 'Context/MyContextProvider';
import { useTemplates } from 'hooks/api';
import PermissionChecker from '../Utils/PermissionChecker';
import NewMobilePreview from '../Campaign/newMobilePreview';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import Flex from 'components/shared-components/Flex';
import { motion, AnimatePresence } from 'framer-motion';

const { confirm } = Modal;

const Templates = () => {

    const { HandleExport, isMobile } = useMyContext();
    const { templates, refetch: refetchTemplates, isFetching: isFetchingTemplates } = useTemplates();

    const [loading, setLoading] = useState(false);
    const [templateList, setTemplateList] = useState([]);

    // refs for table and preview
    const tableRef = useRef(null);
    const previewRef = useRef(null);
    const [tableHeight, setTableHeight] = useState(null);
    const [lastScrollY, setLastScrollY] = useState(null);

    useEffect(() => {
        filerTemplate()
    }, [templates]);

    const filerTemplate = () => {
        if (templates.length > 0) {
            setLoading(true)
            const nonCarouselTemplates = templates.filter(template =>
                !template.components?.some(comp => comp.type === 'CAROUSEL')
            );
            setTemplateList(nonCarouselTemplates);
            setLoading(false)
        }
    }


    const tableColumns = [
        {
            title: 'Template Name',
            dataIndex: 'name',
            sorter: {
                compare: (a, b) => a.name.length - b.name.length,
            },
        },
        {
            title: 'Language',
            dataIndex: 'language',
            sorter: {
                compare: (a, b) => a.language.length - b.language.length,
            },
        },
        {
            title: 'Template Type',
            dataIndex: 'category',
            //   render: date => <span>{dayjs.unix(date).format('MM/DD/YYYY')} </span>,
            sorter: {
                compare: (a, b) => a.name.length - b.name.length,
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: status => (
                <Tag className="text-capitalize" color={status === 'APPROVED' ? 'cyan' : 'red'}>
                    {status}
                </Tag>
            ),
            sorter: {
                compare: (a, b) => a.status.length - b.status.length,
            },
        },
        {
            title: 'Action',
            dataIndex: 'actions',
             fixed: 'right',
            render: (_, item) => (
                <div className="text-right d-flex">
                    <PermissionChecker permission="Preview Template">
                        <Tooltip title="View">
                            <Button
                                type="primary"
                                className="mr-2"
                                icon={<EyeOutlined />}
                                onClick={() => { showDrawer(item) }}
                                size="small"
                            />
                        </Tooltip>
                    </PermissionChecker>
                    <PermissionChecker permission="Delete Template">
                        <Tooltip title="Delete">
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={() => { showConfirm(item.id, item.name) }}
                            />
                        </Tooltip>
                    </PermissionChecker>
                </div>
            ),
        },
    ];

    function showConfirm(id, name) {
        confirm({
            title: 'Do you want to delete this template?',
            content: "By deleting the template, it will be permanently removed from Meta and can't be restored.",
            onOk() {
                HandleDelete(id, name)
            },
            onCancel() {
                // console.log('Cancel');
            },
        });
    }

    const HandleDelete = async (id, name) => {
        setLoading(true);
        await apiClient
            .delete(`messaging/templates/${name}?hsm_id=${id}`)
            .then((res) => {
                if (res.data.success) {
                    setLoading();
                    refetchTemplates();
                    message.success('Template Deleted Successfully');
                }
            })
            .catch((error) => {
                setLoading();
                message.error("Template Cant't be Deleted");
                console.log(error);
            });
    };


    //drawer states 
    const [open, setOpen] = useState(false);
    const [template, setTemplate] = useState([]);

    const showDrawer = (item) => {
        const { header, body, footer, buttons } = extractTemplateSections(item);
        setTemplate({ header, body, footer, buttons });
        if (isMobile) {
            setLastScrollY(window.scrollY);
        }
        setOpen(true);
    };
    // Scroll to preview after open is true and preview is rendered (fixes first-click issue)
    useEffect(() => {
        if (isMobile && open && previewRef.current) {
            previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [open, isMobile, template]);


    const extractTemplateSections = (template) => {
        let header = null, body = null, footer = null, buttons = null;
        template?.components?.forEach(section => {
            switch (section.type) {
                case "HEADER":
                    header = section;
                    break;
                case "BODY":
                    body = section;
                    break;
                case "FOOTER":
                    footer = section;
                    break;
                case "BUTTONS":
                    buttons = section;
                    break;
                default:
                    // Handle any other section types if needed
                    break;
            }
        });
        return { header, body, footer, buttons };
    }

    const [searchText, setSearchText] = useState('');

    const handleSearch = (value) => {
        setSearchText(value);
        if (value) {
            const searchLower = value.toLowerCase();
            const filtered = templateList.filter((item) =>
                tableColumns.some((column) =>
                    item[column.dataIndex]?.toString().toLowerCase().includes(searchLower)
                )
            );
            setTemplateList(filtered);
        } else {
            filerTemplate()
        }
    };

    useEffect(() => {
        handleSearch(searchText)
    }, [searchText]);

    // Calculate table height after data load (only on mobile)
    useEffect(() => {
        if (isMobile && tableRef.current) {
            setTimeout(() => {
                setTableHeight(tableRef.current.offsetHeight);
            }, 100); // wait for render
        }
    }, [templateList, isMobile]);

    const onClose = () => {
        setOpen(false)
        window.scrollTo({ top: lastScrollY, behavior: 'smooth' });
    }

    return (
        <Row align="top" gutter={ROW_GUTTER}>
            <Col xs={24} sm={24} md={24} lg={open ? 18 : 24}>
                <motion.div
                    layout
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                    <Card bodyStyle={{ padding: '0px' }}>
                        <div className="container-fluid">
                            <Row justify="space-between" align="middle" className="py-4">
                                <Col xs={24} sm={24} md={12} lg={8}>
                                    <h2>Templates</h2>
                                </Col>
                                <Col xs={24} sm={24} md={12} lg={16}>
                                    <Row gutter={[16, 16]} justify="end" align="middle">
                                        <Col xs={6} sm={2} md={2} lg={2}>
                                            <Tooltip title="Refresh">
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    onClick={() => refetchTemplates()}
                                                    loading={isFetchingTemplates}
                                                    block
                                                />
                                            </Tooltip>
                                        </Col>

                                        <Col xs={18} sm={10} md={10} lg={6}>
                                            <Input
                                                allowClear
                                                className="p-2 rounded"
                                                value={searchText}
                                                suffix={<SearchOutlined />}
                                                size="large"
                                                onChange={(e) => setSearchText(e.target.value)}
                                            />
                                        </Col>
 
                                        <Col xs={12} sm={6} md={6} lg={4}>
                                            <Button
                                                icon={<CloudDownloadOutlined />}
                                                onClick={() => HandleExport('export-template', 'templates.xlsx')}
                                                block
                                            >
                                                Export
                                            </Button>
                                        </Col>
                                        <PermissionChecker permission="Create Template">
                                            <Col xs={12} sm={6} md={6} lg={4}>
                                                <Link to="add">
                                                    <Button type="primary" block>
                                                        <PlusOutlined />
                                                        <span>New</span>
                                                    </Button>
                                                </Link>
                                            </Col>
                                        </PermissionChecker>
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                        <Row align="middle">
                            <Col xs={24} sm={24} md={24} lg={24}>
                                <div className="table-responsive"
                                    ref={tableRef}
                                    style={isMobile && tableHeight ? { minHeight: tableHeight } : {}}>
                                    <Table
                                        loading={loading}
                                        columns={tableColumns}
                                        dataSource={templateList}
                                        rowKey="id"
                                        scroll={{ x: 'max-content' }}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </motion.div>
            </Col>
            <AnimatePresence>
                {open && (
                    <Col xs={24} sm={24} md={24} lg={6}>
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 50 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        >
                            <div ref={previewRef}>
                                <Card title={
                                    <Flex alignItems="center" justifyContent="space-between">
                                        <h4 className='p-0 m-0'>Campaign Preview</h4>
                                        <CloseOutlined onClick={onClose} style={{ opacity: '50%' }} />
                                    </Flex>
                                }
                                // style={{maxHeight:'80vh' , overflow:'auto'}}
                                >
                                    <NewMobilePreview
                                        mediaRequired={false}
                                        closable={true}
                                        requestType={'template'}
                                        template={template}
                                    />
                                </Card>
                            </div>
                        </motion.div>
                    </Col>
                )}
            </AnimatePresence>
        </Row>
    );
};

export default Templates
